import { Logo } from '@/components/logo';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Href, Link } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Guideline 3.1.1: no iOS o site não mostra preço fora do StoreKit.
const hidePlansOnIOS = Platform.OS === 'ios';

export const SITE_NAV_LINKS = [
  { label: 'Funcionalidades', id: 'funcionalidades' },
  { label: 'Painel', id: 'beneficios' },
  { label: 'Como funciona', id: 'como-funciona' },
  { label: 'Planos', id: 'planos' },
].filter((link) => !(hidePlansOnIOS && link.id === 'planos'));

export interface SiteNavProps {
  /** Na home, rola até a seção. Fora dela, os itens levam para a home. */
  onNavigate?: (id: string) => void;
}

/**
 * Item de navegação. Na web é sempre link de verdade (`<a href="/#planos">`),
 * que buscador segue e que abre em nova aba; na home o clique rola até a seção
 * (a seção tem `nativeID` igual ao `id`). No app nativo, botão que rola.
 */
export function SiteNavItem({
  id,
  onNavigate,
  children,
}: SiteNavProps & { id: string; children: React.ReactNode }) {
  if (Platform.OS === 'web') {
    return (
      <Link
        href={`/#${id}` as Href}
        asChild
        onPress={
          onNavigate
            ? (event) => {
                event.preventDefault();
                onNavigate(id);
              }
            : undefined
        }
      >
        <Pressable accessibilityRole="link">{children}</Pressable>
      </Link>
    );
  }
  if (onNavigate) return <Pressable onPress={() => onNavigate(id)}>{children}</Pressable>;
  return (
    <Link href="/" asChild>
      <Pressable accessibilityRole="link">{children}</Pressable>
    </Link>
  );
}

/** Cabeçalho do site público: o mesmo na home e nas páginas de SEO. */
export function SiteHeader({ onNavigate }: SiteNavProps) {
  const insets = useSafeAreaInsets();

  return (
    <View
      className="supports-[backdrop-filter]:bg-background/70 sticky top-0 z-50 border-b border-neutral-900/5 bg-background backdrop-blur"
      style={Platform.OS !== 'web' ? { paddingTop: insets.top } : undefined}
    >
      <View className="px-4 lg:px-8">
        <View className="flex min-h-16 flex-row items-center justify-between py-2">
          <Link href="/" asChild>
            <Pressable accessibilityRole="link" accessibilityLabel="Controlapag, página inicial">
              <Logo size={44} color="tint" />
            </Pressable>
          </Link>
          <View className="hidden flex-row items-center gap-6 md:flex">
            {SITE_NAV_LINKS.map((link) => (
              <SiteNavItem key={link.id} id={link.id} onNavigate={onNavigate}>
                <ThemedText className="font-medium text-sm text-neutral-600">{link.label}</ThemedText>
              </SiteNavItem>
            ))}
          </View>
          <View className="flex flex-row items-center gap-3">
            {/* No celular só cabe o botão: "Entrar" aparece a partir de 640px (o cadastro tem o link de entrar). */}
            <Link href="/(auth)/login" asChild>
              <Pressable accessibilityRole="link" className="hidden h-10 justify-center px-2 sm:flex">
                <ThemedText className="font-semibold text-sm text-neutral-700">Entrar</ThemedText>
              </Pressable>
            </Link>
            <Link href="/(auth)/register" asChild>
              <Button size="sm" title="Criar conta grátis" />
            </Link>
          </View>
        </View>
      </View>
    </View>
  );
}
